import { useMemo, useState } from "react";
import { FaCopy, FaCheck, FaSave, FaPlus, FaTimes } from "react-icons/fa";
import { createUtmLink } from "JS/API";
import UtmLinkHistory from "Components/UtmLinkHistory";

const DEFAULT_LANDING_URL = "https://www.markpick.co.kr/";
const CUSTOM = "__custom__";

const SOURCE_PRESETS = [
  "naver",
  "instagram",
  "google",
  "kakao",
  "facebook",
  "youtube",
];

const MEDIUM_PRESETS = ["blog", "cpc", "sns", "email", "banner", "influencer"];

// 대문자/공백이 섞이면 서로 다른 값으로 집계되므로 입력 단계에서 통일한다
const normalize = (value) => value.trim().toLowerCase().replace(/\s+/g, "_");

const styles = {
  layout: {
    display: "flex",
    alignItems: "flex-start",
    gap: "24px",
  },
  side: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "28px",
    backgroundColor: "#ffffff",
    flex: "0 0 520px",
  },
  cardTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "4px",
  },
  cardDesc: {
    fontSize: "13px",
    color: "#9ca3af",
    marginBottom: "24px",
  },
  fieldList: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fieldRow: {
    display: "flex",
    gap: "16px",
  },
  fieldHalf: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#374151",
  },
  required: {
    color: "#dc2626",
    marginLeft: "2px",
  },
  hint: {
    fontSize: "12px",
    color: "#9ca3af",
  },
  inputRow: {
    display: "flex",
    gap: "8px",
  },
  input: {
    flex: 1,
    minWidth: 0,
    padding: "9px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1f2937",
  },
  select: {
    flex: 1,
    padding: "9px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1f2937",
    backgroundColor: "#ffffff",
    minWidth: "160px",
  },
  previewBox: {
    marginTop: "24px",
    padding: "16px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#f9fafb",
  },
  previewLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#6b7280",
    marginBottom: "8px",
  },
  previewUrl: {
    fontSize: "14px",
    color: "#1f2937",
    wordBreak: "break-all",
    lineHeight: 1.6,
  },
  previewError: {
    fontSize: "14px",
    color: "#dc2626",
  },
  copyBtn: {
    marginTop: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    cursor: "pointer",
  },
  copyBtnDone: {
    backgroundColor: "#16a34a",
  },
  copyBtnDisabled: {
    backgroundColor: "#d1d5db",
    cursor: "not-allowed",
  },
  postRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  rowBtn: {
    flexShrink: 0,
    width: "32px",
    height: "36px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    color: "#9ca3af",
    cursor: "pointer",
  },
  rowBtnDisabled: {
    color: "#e5e7eb",
    cursor: "not-allowed",
  },
  addBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    fontSize: "13px",
    fontWeight: "600",
    borderWidth: "1px",
    borderStyle: "dashed",
    borderColor: "#cbd5e1",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    color: "#374151",
    cursor: "pointer",
  },
  saveTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "16px",
  },
  saveCard: {
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "24px 28px",
    backgroundColor: "#ffffff",
  },
  saveSection: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  saveBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#374151",
    color: "#ffffff",
    cursor: "pointer",
  },
  saveMsg: {
    fontSize: "13px",
    color: "#16a34a",
  },
  saveErr: {
    fontSize: "13px",
    color: "#dc2626",
  },
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
};

const UtmLinkBuilderTab = ({ onError }) => {
  const [landingUrl, setLandingUrl] = useState(DEFAULT_LANDING_URL);
  const [sourceSelect, setSourceSelect] = useState(SOURCE_PRESETS[0]);
  const [sourceCustom, setSourceCustom] = useState("");
  const [mediumSelect, setMediumSelect] = useState("");
  const [mediumCustom, setMediumCustom] = useState("");
  const [campaign, setCampaign] = useState("");
  const [copied, setCopied] = useState(false);
  const [saveUrl, setSaveUrl] = useState("");
  const [posts, setPosts] = useState([{ memo: "", postUrl: "" }]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const source = normalize(
    sourceSelect === CUSTOM ? sourceCustom : sourceSelect
  );
  const medium = normalize(
    mediumSelect === CUSTOM ? mediumCustom : mediumSelect
  );
  const campaignValue = normalize(campaign);

  const { url, error } = useMemo(() => {
    if (!landingUrl.trim()) {
      return { url: "", error: "랜딩 URL을 입력해 주세요." };
    }
    let parsed;
    try {
      parsed = new URL(landingUrl.trim());
    } catch (e) {
      return {
        url: "",
        error: "랜딩 URL 형식이 올바르지 않습니다. (https:// 포함)",
      };
    }
    if (!source) {
      return { url: "", error: "utm_source는 필수입니다." };
    }
    parsed.searchParams.set("utm_source", source);
    if (medium) parsed.searchParams.set("utm_medium", medium);
    else parsed.searchParams.delete("utm_medium");
    if (campaignValue) parsed.searchParams.set("utm_campaign", campaignValue);
    else parsed.searchParams.delete("utm_campaign");
    return { url: parsed.toString(), error: "" };
  }, [landingUrl, source, medium, campaignValue]);

  const saveTarget = useMemo(() => {
    const trimmed = saveUrl.trim();
    if (!trimmed) return null;
    let parsed;
    try {
      parsed = new URL(trimmed);
    } catch (e) {
      return null;
    }
    return {
      url: parsed.toString(),
      source: parsed.searchParams.get("utm_source") || "",
      medium: parsed.searchParams.get("utm_medium") || "",
      campaign: parsed.searchParams.get("utm_campaign") || "",
    };
  }, [saveUrl]);

  const handleCopy = async () => {
    if (!url) return;
    await copyText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const updatePost = (index, key, value) =>
    setPosts((prev) =>
      prev.map((post, i) => (i === index ? { ...post, [key]: value } : post))
    );

  const addPost = () => setPosts((prev) => [...prev, { memo: "", postUrl: "" }]);

  const removePost = (index) =>
    setPosts((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));

  const handleSave = async () => {
    if (!saveTarget) {
      setSaveMsg({ type: "error", text: "저장할 링크를 올바른 URL 형식으로 입력해 주세요. (https:// 포함)" });
      return;
    }
    if (!saveTarget.source) {
      setSaveMsg({ type: "error", text: "utm_source가 없는 링크입니다. UTM 파라미터가 붙은 링크를 넣어 주세요." });
      return;
    }
    const filledPosts = posts
      .filter((post) => post.memo.trim())
      .map((post) => ({ memo: post.memo.trim(), post_url: post.postUrl.trim() }));
    if (filledPosts.length === 0) {
      setSaveMsg({ type: "error", text: "어디에 뿌릴 링크인지 배포처 메모를 최소 한 건 입력해 주세요." });
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await createUtmLink({
        url: saveTarget.url,
        source: saveTarget.source,
        medium: saveTarget.medium,
        campaign: saveTarget.campaign,
        posts: filledPosts,
        memo: filledPosts[0].memo,
        post_url: filledPosts[0].post_url,
      });
      if (typeof res !== "object" || !res?.data) {
        onError?.(res);
        setSaveMsg({ type: "error", text: "저장에 실패했습니다." });
        return;
      }
      setSaveUrl("");
      setPosts([{ memo: "", postUrl: "" }]);
      setRefreshKey((k) => k + 1);
      setSaveMsg({ type: "ok", text: "내역에 저장되었습니다." });
      setTimeout(() => setSaveMsg(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.layout}>
      <div style={styles.card}>
        <div style={styles.cardTitle}>UTM 링크 만들기</div>
        <div style={styles.cardDesc}>
          값은 자동으로 소문자로 바뀌고 공백은 _ 로 변경됩니다.
        </div>

        <div style={styles.fieldList}>
          <div style={styles.field}>
            <span style={styles.label}>랜딩 URL</span>
            <input
              style={styles.input}
              type="text"
              value={landingUrl}
              placeholder={DEFAULT_LANDING_URL}
              onChange={(e) => setLandingUrl(e.target.value)}
            />
            <span style={styles.hint}>
              링크를 눌렀을 때 도착할 마크픽 페이지 주소
            </span>
          </div>

          <div style={styles.fieldRow}>
            <div style={{ ...styles.field, ...styles.fieldHalf }}>
              <span style={styles.label}>
                utm_source (유입 채널)<span style={styles.required}>*</span>
              </span>
              <div style={styles.inputRow}>
                <select
                  style={styles.select}
                  value={sourceSelect}
                  onChange={(e) => setSourceSelect(e.target.value)}
                >
                  {SOURCE_PRESETS.map((preset) => (
                    <option key={preset} value={preset}>
                      {preset}
                    </option>
                  ))}
                  <option value={CUSTOM}>직접 입력</option>
                </select>
                {sourceSelect === CUSTOM && (
                  <input
                    style={styles.input}
                    type="text"
                    value={sourceCustom}
                    placeholder="예: toss, danggeun"
                    onChange={(e) => setSourceCustom(e.target.value)}
                  />
                )}
              </div>
            </div>

            <div style={{ ...styles.field, ...styles.fieldHalf }}>
              <span style={styles.label}>utm_medium (유입 방식)</span>
              <div style={styles.inputRow}>
                <select
                  style={styles.select}
                  value={mediumSelect}
                  onChange={(e) => setMediumSelect(e.target.value)}
                >
                  <option value="">선택 안 함</option>
                  {MEDIUM_PRESETS.map((preset) => (
                    <option key={preset} value={preset}>
                      {preset}
                    </option>
                  ))}
                  <option value={CUSTOM}>직접 입력</option>
                </select>
                {mediumSelect === CUSTOM && (
                  <input
                    style={styles.input}
                    type="text"
                    value={mediumCustom}
                    placeholder="예: newsletter"
                    onChange={(e) => setMediumCustom(e.target.value)}
                  />
                )}
              </div>
            </div>
          </div>

          <div style={styles.field}>
            <span style={styles.label}>utm_campaign (캠페인 이름)</span>
            <input
              style={styles.input}
              type="text"
              value={campaign}
              placeholder="예: 2608_signup_event"
              onChange={(e) => setCampaign(e.target.value)}
            />
            <span style={styles.hint}>
              마케팅 활동 하나에 붙이는 이름. 활동 단위 성과를 보려면 활동마다
              다르게 정해 주세요.
            </span>
          </div>
        </div>

        <div style={styles.previewBox}>
          <div style={styles.previewLabel}>완성된 링크</div>
          {error ? (
            <div style={styles.previewError}>{error}</div>
          ) : (
            <div style={styles.previewUrl}>{url}</div>
          )}
        </div>

        <button
          style={{
            ...styles.copyBtn,
            ...(copied ? styles.copyBtnDone : {}),
            ...(!url ? styles.copyBtnDisabled : {}),
          }}
          onClick={handleCopy}
          disabled={!url}
        >
          {copied ? <FaCheck size={13} /> : <FaCopy size={13} />}
          {copied ? "복사됨!" : "복사하기"}
        </button>
      </div>

      <div style={styles.side}>
        <div style={styles.saveCard}>
          <div style={styles.saveTitle}>내역에 저장</div>
          <div style={styles.saveSection}>
            <div style={styles.field}>
              <span style={styles.label}>
                저장할 UTM 링크<span style={styles.required}>*</span>
              </span>
              <input
                style={styles.input}
                type="text"
                value={saveUrl}
                placeholder="예: https://www.markpick.co.kr/?utm_source=naver&utm_medium=blog"
                onChange={(e) => setSaveUrl(e.target.value)}
              />
              <span style={styles.hint}>
                링크에 붙은 utm_source · utm_medium · utm_campaign 을 그대로 읽어
                기록합니다.
              </span>
            </div>
            <div style={styles.field}>
              <span style={styles.label}>
                배포처<span style={styles.required}>*</span>
              </span>
              {posts.map((post, index) => (
                <div key={index} style={styles.postRow}>
                  <input
                    style={styles.input}
                    type="text"
                    value={post.memo}
                    placeholder="예: 네이버 블로그 8월 이벤트 글"
                    onChange={(e) => updatePost(index, "memo", e.target.value)}
                  />
                  <input
                    style={styles.input}
                    type="text"
                    value={post.postUrl}
                    placeholder="게시물 주소 (선택)"
                    onChange={(e) => updatePost(index, "postUrl", e.target.value)}
                  />
                  <button
                    style={{
                      ...styles.rowBtn,
                      ...(posts.length <= 1 ? styles.rowBtnDisabled : {}),
                    }}
                    onClick={() => removePost(index)}
                    disabled={posts.length <= 1}
                    aria-label="배포처 삭제"
                  >
                    <FaTimes size={11} />
                  </button>
                </div>
              ))}
              <button style={styles.addBtn} onClick={addPost}>
                <FaPlus size={10} />
                배포처 추가
              </button>
              <span style={styles.hint}>
                같은 링크를 여러 곳에 뿌렸으면 배포처를 여러 건 등록하세요.
              </span>
            </div>
            <button
              style={{
                ...styles.saveBtn,
                ...(!saveTarget || saving ? styles.copyBtnDisabled : {}),
              }}
              onClick={handleSave}
              disabled={!saveTarget || saving}
            >
              <FaSave size={13} />
              {saving ? "저장 중..." : "내역에 저장"}
            </button>
            {saveMsg && (
              <span style={saveMsg.type === "ok" ? styles.saveMsg : styles.saveErr}>
                {saveMsg.text}
              </span>
            )}
          </div>
        </div>

        <UtmLinkHistory refreshKey={refreshKey} onError={onError} />
      </div>
    </div>
  );
};

export default UtmLinkBuilderTab;
